import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { JWT_STORAGE_KEY } from '@/lib/api.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import type { AuthUser } from '@/context/AuthContext.tsx';

// ─── Tipos de la respuesta del backend ───────────────────────────────────────

// Subset de campos que el endpoint POST /auth/login devuelve en data.user.
// Refleja la tabla users: id, tenant_id, nombre, email, rol, activo.
interface UserPublic {
  id:         string;
  tenant_id:  string;
  nombre:     string;
  email:      string;
  rol:        string;
  activo:     number | boolean;
}

interface LoginApiResponse {
  status: 'ok';
  data: {
    token:      string;
    expiresIn:  string;
    user:       UserPublic;
    tenantSlug: string;
  };
}

// ─── Componente ───────────────────────────────────────────────────────────────

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [error, setError]           = useState<string | null>(null);
  const [isLoading, setIsLoading]   = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!tenantSlug.trim()) {
      setError('Introduce el identificador de tu empresa.');
      return;
    }

    setIsLoading(true);

    try {
      // Guarda el slug ANTES de la petición para que el interceptor de Axios
      // lo inyecte como cabecera x-tenant-slug en esta y futuras peticiones.
      // localStorage persiste entre recargas; sessionStorage como fallback.
      const normalizedSlug = tenantSlug.trim().toLowerCase();
      localStorage.setItem('tenant_slug', normalizedSlug);
      sessionStorage.setItem('tenant_slug', normalizedSlug);

      console.log('[v0] login attempt slug:', normalizedSlug, 'email:', email.trim().toLowerCase());
      const { data: res } = await api.post<LoginApiResponse>('/auth/login', {
        email:    email.trim().toLowerCase(),
        password,
      });

      const { token, user } = res.data;

      // Construye el AuthUser normalizando los campos de la BD
      const authUser: AuthUser = {
        id:         user.id,
        email:      user.email,
        name:       user.nombre,
        role:       user.rol,
        tenantId:   user.tenant_id,
        tenantSlug: tenantSlug.trim().toLowerCase(),
      };

      // Persiste JWT y estado global
      localStorage.setItem(JWT_STORAGE_KEY, token);
      login(token, authUser);

      // Redirige según el rol: superadmin va al panel maestro, el resto a su tenant
      if (authUser.role === 'superadmin') {
        navigate('/admin', { replace: true });
      } else {
        navigate(`/${authUser.tenantSlug}/dashboard`, { replace: true });
      }
    } catch (err: unknown) {
      console.log('[v0] login error:', err);
      sessionStorage.removeItem('tenant_slug');

      if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response
      ) {
        const data = (err.response as { data?: { message?: string } }).data;
        setError(data?.message ?? 'Credenciales incorrectas.');
      } else {
        setError('No se pudo conectar con el servidor. Intenta de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--color-bg)',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          padding: '2.5rem 2rem',
          borderRadius: 12,
          width: 380,
          boxShadow: '0 4px 32px rgba(0,0,0,.16)',
        }}
      >
        {/* Cabecera */}
        <div style={{ marginBottom: '1.75rem' }}>
          <h1
            style={{
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'var(--color-text)',
              letterSpacing: '-0.01em',
            }}
          >
            Impulsa ERP
          </h1>
          <p
            style={{
              margin: '0.25rem 0 0',
              fontSize: '0.8125rem',
              color: 'var(--color-muted)',
            }}
          >
            Inicia sesion en tu empresa
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} noValidate>
          <Field label="Empresa (slug)">
            <input
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              placeholder="mi-empresa"
              autoComplete="organization"
              autoFocus
              required
              style={inputStyle}
            />
          </Field>

          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com"
              autoComplete="email"
              required
              style={inputStyle}
            />
          </Field>

          <Field label="Contrasena">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              style={inputStyle}
            />
          </Field>

          {/* Error */}
          {error && (
            <p
              role="alert"
              style={{
                margin: '0 0 1rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(239,68,68,.1)',
                border: '1px solid rgba(239,68,68,.3)',
                borderRadius: 6,
                fontSize: '0.8125rem',
                color: '#ef4444',
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '0.625rem 1rem',
              background: isLoading ? 'var(--color-muted)' : 'var(--color-primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '0.9375rem',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {isLoading ? 'Verificando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Helpers de estilo ────────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label
        style={{
          display: 'block',
          marginBottom: '0.375rem',
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: 'var(--color-text)',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: '0.9375rem',
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
};
